import React from "react";
import styled from "styled-components";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import * as ROUTES from "constants/routes";
import CustomLink from "components/shared/CustomLink";

import {
  useTable,
  usePagination,
  useFilters,
  useGlobalFilter,
} from "react-table";
import { Pagination } from "antd";

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

const AllJobsTable = ({
  columns,
  data,
  fetchData,
  status,
  pageCount: controlledPageCount,
  resultCount: controlledResultCount,
  screens,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize, hiddenColumns },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 1, pageSize: 30 }, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
      autoResetFilters: false,
      autoResetPage: false,
    },
    usePagination
  );

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  // Component Function
  const handleSizeChange = (_, pageSize) => {
    setPageSize(pageSize);
  };

  // Render the UI for your table
  return (
    <>
      <TableContainer>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    style={{ textAlign: column.textAlign || "left" }}
                  >
                    {column.render("Header")}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? " 🔽"
                          : " 🔼"
                        : ""}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {(status === "loading" || status === "idle") && (
            <tbody>
              <tr>
                <td colSpan="10000">
                  <StyledLoading>
                    <Spin indicator={<StyledLoadingOutlined spin />} />
                    <p>Getting more data..</p>
                  </StyledLoading>
                </td>
              </tr>
            </tbody>
          )}
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps()}
                        style={{ width: cell.column.width }}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableContainer>
      {/* 
        Pagination can be built however you'd like. 
        This is just a very basic UI implementation:
      */}
      <PaginationContainer>
        <Pagination
          current={pageIndex}
          total={controlledResultCount}
          onChange={(e) => gotoPage(e)}
          defaultPageSize={5}
          pageSize={pageSize}
          showSizeChanger
          onShowSizeChange={handleSizeChange}
          pageSizeOptions={[30, 50, 100]}
          showTotal={(total) => `Total ${total} items`}
        />
      </PaginationContainer>
    </>
  );
};

const TableContainer = styled.div`
  overflow-x: auto;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing[4]};
`;

const StyledLoading = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.grey[500]};
  font-weight: 500;
  p {
    margin: 0;
    margin-left: ${(props) => props.theme.spacing[2]};
    align-items: flex-end;
  }
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  font-size: 16px;
  color: ${(props) => props.theme.colors.grey[400]};
`;

export default AllJobsTable;
